import {
  comparisonPages as rawComparisonPages,
  corePages as rawCorePages,
  docsHub as rawDocsHub,
  allSemanticPages as rawAllSemanticPages,
} from './data.js';
import { docsApps } from './apps.js';
import { docsConcepts } from './concepts.js';
import { docsUseCases } from './useCases.js';
import {
  docsAppDirectoryEntries,
  docsAppsHubPage,
  getDocsAppDirectoryPage,
} from './appDirectory.js';
import type {
  SemanticHubPage,
  SemanticPage,
  SemanticResolvedLink,
} from './types.js';

export const corePages = rawCorePages as SemanticPage[];
export const comparisonPages = rawComparisonPages as SemanticPage[];
export const docsHubPage = rawDocsHub as SemanticHubPage;
export const semanticPages = rawAllSemanticPages as SemanticPage[];

const pageByPathEntries: Array<readonly [string, SemanticPage | SemanticHubPage]> = [
  [docsHubPage.path, docsHubPage],
  [docsAppsHubPage.path, docsAppsHubPage],
  ...semanticPages.map((page) => [page.path, page] as const),
  ...docsAppDirectoryEntries.map((entry) => [entry.path, entry.page] as const),
];

const pageByPath = new Map<string, SemanticPage | SemanticHubPage>(pageByPathEntries);

const pageByRouteKeyEntries: Array<readonly [string, SemanticPage]> = [
  ...semanticPages.map(
    (page) =>
      [
        `${page.pageType}:${page.slug}`,
        page,
      ] as const,
  ),
  ...docsAppDirectoryEntries.map(
    (entry) =>
      [
        `${entry.page.pageType}:${entry.page.slug}`,
        entry.page,
      ] as const,
  ),
];

const pageByRouteKey = new Map<string, SemanticPage>(pageByRouteKeyEntries);

export const semanticStaticPaths = Array.from(
  new Set([
    docsHubPage.path,
    docsAppsHubPage.path,
    ...semanticPages.map((page) => page.path),
    ...docsAppDirectoryEntries.map((entry) => entry.path),
  ]),
);

export function getSemanticPageByPath(pathname: string) {
  return pageByPath.get(pathname) ?? null;
}

export function getDocsAppPage(slug: string) {
  return docsApps.find((page) => page.slug === slug) ?? getDocsAppDirectoryPage(slug);
}

export function getDocsConceptPage(slug: string) {
  return docsConcepts.find((page) => page.slug === slug) ?? null;
}

export function getDocsUseCasePage(slug: string) {
  return docsUseCases.find((page) => page.slug === slug) ?? null;
}

export function getPageByRouteKey(pageType: SemanticPage['pageType'], slug: string) {
  return pageByRouteKey.get(`${pageType}:${slug}`) ?? null;
}

export function resolveSemanticLink(path: string): SemanticResolvedLink {
  const page = getSemanticPageByPath(path);

  if (!page) {
    return {
      path,
      label: path,
    };
  }

  return {
    path,
    label: page.breadcrumbLabel || page.title,
    description: page.summary,
  };
}

export function resolveSemanticLinks(paths: string[]) {
  return paths.map(resolveSemanticLink);
}